
/**
 * Suspense-compatible entry for an async-resolved value.
 * Follows React's throw-promise convention so it works inside <Suspense>.
 */
interface SuspenseEntry {
  status: "pending" | "resolved" | "rejected";
  value?: string;
  error?: unknown;
  promise?: Promise<void>;
}

/** Module-level suspense cache keyed by an arbitrary string (e.g. asset endpoint). */
const suspenseCache = new Map<string, SuspenseEntry>();

/**
 * Resolves an async URL in a Suspense-friendly way.
 *
 * Usage inside a component wrapped by <Suspense>:
 * ```tsx
 * const url = useAssetUrl(`model:${id}`, () => api.resolveModelUrl(id));
 * const { scene } = useGLTF(url);
 * ```
 *
 * On first call the hook throws a promise (Suspense protocol).
 * On subsequent renders it returns the resolved URL synchronously.
 * The result is cached across component instances and re-renders.
 */
export function useAssetUrl(
  key: string,
  resolver: () => Promise<string>,
): string {
  const entry = suspenseCache.get(key);

  if (!entry) {
    // First access — kick off resolution and throw the promise for Suspense
    const newEntry: SuspenseEntry = { status: "pending" };
    newEntry.promise = resolver().then(
      (url) => {
        newEntry.status = "resolved";
        newEntry.value = url;
      },
      (err) => {
        newEntry.status = "rejected";
        newEntry.error = err;
      },
    );
    suspenseCache.set(key, newEntry);
    throw newEntry.promise;
  }

  if (entry.status === "pending") {
    throw entry.promise;
  }

  if (entry.status === "rejected") {
    // Keep the rejected entry cached so the error boundary shows its
    // fallback permanently instead of re-triggering fetches in a loop.
    throw entry.error;
  }

  return entry.value!;
}

/**
 * Read a resolved URL from the suspense cache without throwing.
 * Returns `undefined` if the key has not been resolved yet.
 */
export function getResolvedAssetUrl(key: string): string | undefined {
  const entry = suspenseCache.get(key);
  return entry?.status === "resolved" ? entry.value : undefined;
}

/**
 * Clear the suspense cache (useful for testing or forced refresh).
 */
export function clearAssetUrlCache(): void {
  suspenseCache.clear();
}

/**
 * Evict all suspense cache entries whose resolved value matches `url`.
 * Called by the model cache evictor when a GLTF URL is evicted.
 */
export function evictAssetUrlByValue(url: string): void {
  for (const [key, entry] of suspenseCache) {
    if (entry.status === "resolved" && entry.value === url) {
      suspenseCache.delete(key);
    }
  }
}
